/**
 * remark plugin: render Pandoc-style `~sub~` / `^sup^` markers as real
 * <sub>/<sup> elements — `H~2~O`, `CO~2~`, `×10^9^/L`, `m^2^`, `C~max~`.
 *
 * The grammar (strict: non-empty, no whitespace, no `%`, ≤15 chars, closing
 * marker required) lives in formatter/markdownParser.ts (RE_SUB_SUP) and is a
 * verbatim mirror of SUB_MARKER_RE / SUP_MARKER_RE in
 * src/csr_sim/draft_sanitizer.py — the SSOT. Prose tildes/carets (`~50%`,
 * `10^9`) never match and stay literal text.
 *
 * IMPORTANT: every ReactMarkdown surface using this plugin must also pass
 * remark-gfm as `[remarkGfm, { singleTilde: false }]` — GFM's default
 * single-tilde strikethrough consumes `~2~` at the micromark layer, before
 * this transformer ever sees the text node.
 *
 * Hand-rolled tree walk (no unist-util-visit dependency), mirroring the
 * remarkSoftBreaks pattern in DraftTab.tsx.
 */

import { RE_SUB_SUP } from "./formatter/markdownParser";

interface MdNode {
  type?: string;
  value?: string;
  children?: unknown[];
  data?: { hName?: string };
}

export function remarkSubSup() {
  return (tree: unknown) => {
    const walk = (node: MdNode | null) => {
      if (!node || !Array.isArray(node.children)) return;
      const next: unknown[] = [];
      for (const child of node.children as MdNode[]) {
        if (
          child.type === "text" &&
          typeof child.value === "string" &&
          (child.value.includes("~") || child.value.includes("^"))
        ) {
          const text = child.value;
          let lastEnd = 0;
          for (const match of text.matchAll(RE_SUB_SUP)) {
            if ((match.index ?? 0) > lastEnd) {
              next.push({ type: "text", value: text.slice(lastEnd, match.index) });
            }
            const isSub = match[1] !== undefined;
            next.push({
              type: "subSup",
              data: { hName: isSub ? "sub" : "sup" },
              children: [{ type: "text", value: isSub ? match[1] : match[2] }],
            });
            lastEnd = (match.index ?? 0) + match[0].length;
          }
          if (lastEnd === 0) {
            // No marker matched — keep the original node untouched.
            next.push(child);
          } else if (lastEnd < text.length) {
            next.push({ type: "text", value: text.slice(lastEnd) });
          }
        } else {
          walk(child);
          next.push(child);
        }
      }
      node.children = next;
    };
    walk(tree as MdNode);
  };
}
